import React, { useState } from 'react';
import { Brodcrumb } from '../../components/ui/UtilsLayout';
import { iconsPath, imagePath, videoPath } from '../../utils/imagePath';
import { BackgroundWithCurve, CaseStudiesImageCard, CaseStudyHeading, ImpactCard, Section } from '../../utils/CaseStudiesComponents';
import VideoShimmer from '../../components/ShimmerEffects/VideoShimmer';

// CaseStudy Component
const RaghvaProjects = () => {
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  return (
    <div className="flex justify-center px-5 md:px-10 lg:px-16  items-center gap-6 text-white pb-16 relative">
      <BackgroundWithCurve />
      <div className="max-w-[1200px] w-full  pt-24 md:pt-32 relative z-10">
        {/* Breadcrumb Navigation */}
        <Brodcrumb
          data={{
            name: 'Raghava Projects',
            item: [
              { name: 'Industry Use Cases', path: '/all-industries' },
              {
                name: 'Architecture & Real Estate',
                path: '/industries/realstate-architecture',
              },
            ],
          }}
        />

        <div className="my-10 flex flex-col w-full gap-10">
          {/* Header Section with Logo */}
          <div className="flex gap-3 md:gap-5 lg:gap-10">
            <div className="flex justify-center items-center  bg-[#1D3E3C] shadow-[0px_0px_4px_1px_rgba(0,0,0,0.25)] h-20 w-32 md:h-20 md:w-20 lg:h-24 lg:w-24 rounded-lg">
              {/* Company Logo */}
              {imagePath?.RaghvaLogo ? (
                <img
                  className="h-16 w-16 md:h-16 md:w-16 lg:h-20 lg:w-20 object-contain"
                  src={imagePath?.RaghvaLogo}
                  alt="Raghava Logo"
                />
              ) : (
                <span className="text-gray-400 text-xs">No Logo</span>
              )}
            </div>

            {/* Case Study Heading */}
            <div className="flex flex-col gap-2 md:gap-5 justify-center">
              <CaseStudyHeading
                title="Raghava Projects"
                desc="Driving Real Estate Results Through Immersive Tech."
              />
            </div>
          </div>

          <div className="relative">
            {/* Video Container - Responsive aspect ratio */}
            <div className="relative w-full aspect-video bg-[#31333A] shadow-[0px_0px_21px_4px_rgba(0,0,0,0.25)] flex justify-center items-center ">
              {!isVideoLoaded && <VideoShimmer className="absolute inset-0" />}
              {videoPath?.RaghavaVideo && (
                <video
                  src={videoPath.RaghavaVideo}
                  autoPlay
                  muted
                  loop
                  playsInline
                  onLoadedData={() => setIsVideoLoaded(true)}
                  className={`w-full h-full object-cover transition-opacity duration-300 ${
                    isVideoLoaded ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  Your browser does not support the video tag.
                </video>
              )}

              {/* {iconsPath?.QRIcon && (
                <div className="absolute -top-4 -right-4 md:-top-6 md:-right-6 lg:-top-8 lg:-right-8 bg-[#fff] p-2 md:p-3 rounded-[10px]">
                  <img
                    src={iconsPath.QRIcon}
                    alt="QR Code"
                    className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24"
                  />
                </div>
              )} */}
            </div>
          </div>

          {/* Case Study Description */}
          <Section
            title="Overview"
            content={[
              'The real estate industry is no stranger to change — but it has rarely moved this fast. As buying behavior evolves and digital expectations skyrocket, Spatial Grid is enabling real estate brands to meet their customers where they are: in immersive, interactive virtual spaces.',
              'A fast-growing real estate firm, Raghava Projects faced a common challenge:"We have stunning properties, but limited ways to showcase them at scale before they’re built. When customers understand the property and floor plans faster, our sales teams can focus more on meaningful conversations and closing opportunities',
            ]}
          />

          <Section
            title="The Spatial Grid Solution"
            content={[
              'We transformed traditional marketing materials—brochures, site hoardings, and newspaper ads—into fully interactive 3D AR experiences. By scanning a simple QR code, prospective buyers could see a scale model of the building appear directly over the printed asset, explore detailed floor plans anchored to the surface, and interact with hotspots highlighting amenities, interiors, and pricing. This turned every print asset into a portable, immersive showroom. Additionally, we deployed AR location tracking at the construction site, allowing visitors to stand on the actual plot and visualize the completed tower at real scale. Using their smartphone’s GPS, they could experience live AR walkthroughs of layouts, parking areas, amenity zones, and landscaped spaces—without the need for headsets, apps, or downloads.',
            ]}
          />

          {/* Image Grid Section */}
          <div className="flex flex-col gap-3">
            <div className="flex md:flex-row flex-col gap-10 md:gap-20 w-full">
              <CaseStudiesImageCard
                image={imagePath?.Raghva1}
                title="AR Experience Interface"
              />
              <CaseStudiesImageCard
                image={imagePath?.Raghva2}
                title=" AR Property Visualization for ads"
              />
            </div>
          </div>

          {/* Impact Section */}
          <div className="flex flex-col gap-3">
            <Section
              title="Impact"
              content="Across just a few deployments, the results were striking. Qualified leads increased by 47%, with 
              users who engaged with the immersive experiences nearly twice as likely to request a callback or 
              schedule a site visit. Engagement time rose by 62%, as visitors spent far longer interacting with 
              dynamic 3D content compared to static images or PDF brochures. Conversion rates saw a 31% lift, 
              with the immersive walkthroughs helping buyers make clearer, faster decisions and reducing drop-offs
              in the sales funnel. Perhaps most impressive was a 200% surge in remote interactions, enabling 
              overseas and out-of-town buyers to explore the properties virtually and driving significant interest 
              beyond the local market."
            />
          </div>
        </div>

        {/* Impact Section */}
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-5 lg:gap-10">
            {[
              {
                percentage: '+47%',
                description: 'increase in qualified leads',
              },
              {
                percentage: '+62%',
                description: 'increase in engagement time ',
              },
              { percentage: '+31%', description: 'boost in conversion rates' },
              {
                percentage: '+200%',
                description: 'spike in remote interactions',
              },
            ].map((impact, i) => (
              <ImpactCard
                key={i}
                percentage={impact.percentage}
                description={impact.description}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
<CaseStudyHeading />;
<CaseStudiesImageCard />;

export default RaghvaProjects;
